import { type PresetStylesClassStructure } from '../types/presetStylesClassStructure';

export const dialog = {
  root: ({ props, state }): PresetStylesClassStructure => ({
    class: [
      // Shape
      'rounded-lg',
      'shadow-lg',
      'border-0',
      'overflow-hidden ',

      // Size
      'max-h-[90vh]',
      ' w-full max-w-[700px]',
      {
        'my-24': props.position === 'top',
      },
      // Color
      'shadow-md border-solid border-red-400 border-0 border-l-[6px] ',
      // Transitions
      'transform',
      'scale-100',
      'bg-white',
      // Maximized State
      {
        'transition-none': state.maximized,
        'transform-none': state.maximized,
        '!w-screen': state.maximized,
        '!h-screen': state.maximized,
        '!max-h-full': state.maximized,
        '!top-0': state.maximized,
        '!left-0': state.maximized,
      },
    ],
  }),
  header: {
    class: [
      // Flexbox and Alignment
      'flex items-center justify-between',
      'shrink-0',
      'bg-transparent',
      // Spacing
      'py-6 px-8',

      // Shape
      'border-t-0',
      'rounded-tl-lg',
      'rounded-tr-lg',
      'text-serif',

      // Colors
      'bg-surface-0 dark:bg-surface-800',
      'text-negative dark:text-surface-0/80',
    ],
  },
  title: {
    class: ['font-bold  text-3xl bg-transparent'],
  },
  icons: {
    class: ['flex items-center'],
  },
  closeButton: {
    rejectButton: {
      class: ['bg-red-400'],
    },
    class: [
      'relative',

      // Flexbox and Alignment
      'flex items-center justify-center',

      // Size and Spacing
      'mr-2',
      'last:mr-0',
      'w-16 h-16 ',

      // Shape
      'border-0',
      'rounded-full',
      // Colors
      'text-black',
      'bg-red',

      // Transitions
      'transition duration-200 ease-in-out',

      // States
      'hover:text-black/80 dark:hover:text-black/80',
      'hover:bg-black/10 dark:hover:bg-black/20',
      'hover:outline-none hover:ring-black/50  hover:ring focus:ring-inset',

      // Misc
      'overflow-hidden',
    ],
  },

  maximizablebutton: {
    class: [
      'relative',

      // Flexbox and Alignment
      'flex items-center justify-center',

      // Size and Spacing
      'mr-2',
      'last:mr-0',
      'w-12 h-12',

      // Shape
      'border-0',
      'rounded-full',

      // Colors
      'text-surface-500',
      'bg-transparent',

      // Transitions
      'transition duration-200 ease-in-out',

      // States
      'hover:text-surface-700 dark:hover:text-black/80',
      'hover:bg-surface-100 dark:hover:bg-surface-800/80',
      'focus:outline-none focus:outline-offset-0 focus:ring focus:ring-inset',
      'focus:ring-black-400/50 dark:focus:ring-black-300/50',

      // Misc
      'overflow-hidden',
    ],
  },
  closeButtonIcon: {
    class: [
      // Display
      'inline-block',

      // Size
      'w-8',
      'h-8',
    ],
  },
  maximizableicon: {
    class: [
      // Display
      'inline-block',

      // Size
      'w-4',
      'h-4',
    ],
  },
  content: ({ state, instance }): PresetStylesClassStructure => ({
    class: [
      // Spacing
      'flex',
      'gap-4',
      'items-center',

      'px-8',
      'pt-4',
      'pb-12',
      'bg-transparent',

      // Shape
      {
        grow: state.maximized,
        'rounded-bl-lg': !instance.$slots.footer,
        'rounded-br-lg': !instance.$slots.footer,
      },

      // Colors
      'bg-surface-0 dark:bg-surface-800',
      'text-surface dark:text-surface-0/80',
      // Misc
      'overflow-y-auto',
    ],
  }),
  footer: {
    class: [
      // Flexbox and Alignment
      'flex items-center justify-end',
      'shrink-0',
      'text-right',
      'mt-6',
      'gap-4',

      // Spacing
      'px-8',
      'pb-6',

      // Shape
      'border-t-0',
      'rounded-b-lg',

      // Colors
      'bg-surface-0 dark:bg-surface-800',
      'text-surface-700 dark:text-surface-0/80',
    ],
  },
  mask: ({ props, state }): PresetStylesClassStructure => ({
    class: [
      // Transitions
      'transition',
      'duration-200',
      { 'p-5': !state.maximized },

      // Background and Effects
      {
        'bg-black/40': props.modal,
        'backdrop-blur-sm': props.modal,
      },
    ],
  }),
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  transition: ({ props }): any => {
    return props.position === 'top'
      ? {
          enterFromClass: 'opacity-0 scale-75 translate-x-0  -translate-y-full translate-z-0',
          enterActiveClass: 'transition-all  duration-200 ease-out',
          leaveActiveClass: 'transition-all duration-200 ease-out',
          leaveToClass: 'opacity-0 scale-75 translate-x-0 -translate-y-full translate-z-0',
        }
      : props.position === 'bottom'
        ? {
            enterFromClass: 'opacity-0 scale-75 translate-y-full',
            enterActiveClass: 'transition-all duration-200 ease-out',
            leaveActiveClass: 'transition-all duration-200 ease-out',
            leaveToClass: 'opacity-0 scale-75 translate-x-0 translate-y-full translate-z-0',
          }
        : props.position === 'left' || props.position === 'topleft' || props.position === 'bottomleft'
          ? {
              enterFromClass: 'opacity-0 scale-75 -translate-x-full translate-y-0 translate-z-0',
              enterActiveClass: 'transition-all duration-200 ease-out',
              leaveActiveClass: 'transition-all duration-200 ease-out',
              leaveToClass: 'opacity-0 scale-75  -translate-x-full translate-y-0 translate-z-0',
            }
          : props.position === 'right' || props.position === 'topright' || props.position === 'bottomright'
            ? {
                enterFromClass: 'opacity-0 scale-75 translate-x-full translate-y-0 translate-z-0',
                enterActiveClass: 'transition-all duration-200 ease-out',
                leaveActiveClass: 'transition-all duration-200 ease-out',
                leaveToClass: 'opacity-0 scale-75 opacity-0 scale-75 translate-x-full translate-y-0 translate-z-0',
              }
            : {
                enterFromClass: 'opacity-0 scale-75',
                enterActiveClass: 'transition-all duration-200 ease-out',
                leaveActiveClass: 'transition-all duration-200 ease-out',
                leaveToClass: 'opacity-0 scale-75',
              };
  },
};
